import { useRef } from "react";
import { LocationAutoCompleteWithSuggestion } from "@apac/components/ui";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { CoreHotelsComponent, Funnel } from "@b2bportal/core-types";
import {
  LodgingSelectionEnum,
  type PlaceId,
  PlaceIdEnum,
  type Suggestion,
} from "@b2bportal/lodging-api";
import {
  PremierDestinationQuickPicks,
  type PromotedDestinationCity,
  usePremierDestinationPromotion,
} from "@hsbc/portal";
import { fetchLocations, type HotelLocationSuggestion } from "@tenant/hotels";
import { Icon, type LocationAutocompleteProps } from "@tenant/ui";
import clsx from "clsx";
import styles from "./styles.module.scss";

export interface HotelLocationAutocompleteProps
  extends Omit<
    LocationAutocompleteProps<HotelLocationSuggestion>,
    | "fetch"
    | "groupBy"
    | "getOptionLabel"
    | "getOptionIcon"
    | "sortOptions"
    | "id"
  > {
  value?: HotelLocationSuggestion;
  onClose?: () => void;
}

export const HotelLocationAutocomplete = (
  props: HotelLocationAutocompleteProps
) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [block] = useModuleBEM(
    styles,
    CoreHotelsComponent.HotelLocationAutocomplete
  );

  const {
    activePremierDestinations: { hotels: activePremierDestinations },
    enabledPromotions: { hotels: enablePremierDestination },
  } = usePremierDestinationPromotion();

  const onPremierDestinationSelect = (city: PromotedDestinationCity) => {
    const selectedCity = {
      label: city.label,
      id: {
        Id: "Lodgings",
        lodgingSelection: {
          LodgingSelection: "Place",
          placeId: city.placeId,
          placeTypes: ["geocode"],
          searchTerm: city.label,
        },
      },
      trackingPropertiesV2: {},
    };

    props.onChange(selectedCity as Suggestion);
    props.onClose?.();
  };

  return (
    <div className={clsx(block, props.className)}>
      <LocationAutoCompleteWithSuggestion<PlaceId>
        {...props}
        inputRef={inputRef}
        id="hotel-location-selection"
        value={props.value}
        onChange={props.onChange}
        fetch={fetchLocations}
        getOptionLabel={getOptionLabel}
        getOptionIcon={getOptionIcon}
        funnel={Funnel.Lodging}
        renderSuggestions={() => (
          <>
            {enablePremierDestination && (
              <PremierDestinationQuickPicks
                funnel={Funnel.Lodging}
                activePremierDestinations={activePremierDestinations}
                onSelect={onPremierDestinationSelect}
              />
            )}
          </>
        )}
      />
    </div>
  );
};

const getOptionLabel = (value?: HotelLocationSuggestion) => {
  if (
    value?.id.Id === PlaceIdEnum.Lodgings &&
    value.id.lodgingSelection.LodgingSelection === LodgingSelectionEnum.Place
  ) {
    return value.id.lodgingSelection.searchTerm;
  }
  return "";
};

const getOptionIcon = (value?: HotelLocationSuggestion) => {
  return <Icon iconName={IconNameEnum.location} />;
};
